import React, { useState } from "react";
import {
    Box, Button, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, Divider, IconButton, List,
    ListItem, ListItemIcon, ListItemText, MenuItem,
    TextField, Typography, Alert, Stack, Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import SendIcon from "@mui/icons-material/Send";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";

import AuditStatusChip from "./AuditStatusChip";
import { agregarTracking } from "../services/audit.service";

const fmtSize = (b) => {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * AuditTrackingPanel
 *
 * Props:
 *   open        {bool}
 *   onClose     {fn}
 *   onSuccess   {fn}   — se llama después de guardar exitosamente
 *   itemId      {number}
 *   username    {string}
 *   statusActual {object} — { id, codigo, descripcion, color }
 *   estados     {Array}   — lista completa de estados
 *   puedeVerTodo {bool}   — solo auditores/admin pueden cambiar estado
 */
export default function AuditTrackingPanel({
    open, onClose, onSuccess,
    itemId, username,
    statusActual, estados = [],
    puedeVerTodo,
}) {
    const [comentario, setComentario] = useState("");
    const [statusNuevo, setStatusNuevo] = useState("");
    const [archivos, setArchivos] = useState([]);
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState(null);

    const handleClose = () => {
        if (guardando) return;
        setComentario("");
        setStatusNuevo("");
        setArchivos([]);
        setError(null);
        onClose();
    };

    const handleArchivos = (e) => {
        const nuevos = Array.from(e.target.files || []);
        setArchivos((prev) => {
            const existentes = new Set(prev.map((f) => `${f.name}-${f.size}`));
            return [...prev, ...nuevos.filter((f) => !existentes.has(`${f.name}-${f.size}`))];
        });
        e.target.value = "";
    };

    const eliminarArchivo = (idx) =>
        setArchivos((prev) => prev.filter((_, i) => i !== idx));

    const handleGuardar = async () => {
        if (!comentario.trim() && !statusNuevo && !archivos.length) {
            setError("Agregá un comentario, cambiá el estado o adjuntá un archivo.");
            return;
        }

        setGuardando(true);
        setError(null);
        try {
            const tipoAccion = statusNuevo
                ? "CAMBIO_ESTADO"
                : archivos.length
                    ? "ADJUNTO"
                    : "COMENTARIO";

            await agregarTracking(
                itemId,
                {
                    username,
                    status_nuevo: statusNuevo || undefined,
                    tipo_accion: tipoAccion,
                    comentario: comentario.trim() || undefined,
                },
                archivos
            );

            handleClose();
            onSuccess?.();
        } catch (e) {
            setError(
                e?.response?.data?.error ||
                "Error al guardar. Intentá nuevamente."
            );
        } finally {
            setGuardando(false);
        }
    };

    // Estados disponibles para cambiar (excluir el actual)
    const estadosDisponibles = estados.filter(
        (e) => e.id !== statusActual?.id
    );

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 2 } }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight={700}>
                        Registrar avance
                    </Typography>
                    <IconButton size="small" onClick={handleClose} disabled={guardando}>
                        <CloseIcon />
                    </IconButton>
                </Stack>

                {/* Estado actual */}
                {statusActual && (
                    <Stack direction="row" alignItems="center" gap={1} mt={0.5}>
                        <Typography variant="body2" color="text.secondary">
                            Estado actual:
                        </Typography>
                        <AuditStatusChip
                            codigo={statusActual.codigo}
                            descripcion={statusActual.descripcion}
                            color={statusActual.color}
                        />
                    </Stack>
                )}
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ pt: 2 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {/* Comentario */}
                <TextField
                    fullWidth multiline rows={4}
                    label="Comentario / Observación"
                    value={comentario}
                    onChange={(e) => setComentario(e.target.value)}
                    placeholder="Describí el avance, acción tomada o información relevante..."
                    sx={{ mb: 2 }}
                />

                {/* Cambio de estado — solo auditores/admin */}
                {puedeVerTodo && (
                    <TextField
                        select fullWidth
                        label="Cambiar estado"
                        value={statusNuevo}
                        onChange={(e) => setStatusNuevo(e.target.value)}
                        helperText="Opcional — dejá vacío si no cambia el estado"
                        sx={{ mb: 2 }}
                    >
                        <MenuItem value="">— Sin cambio de estado —</MenuItem>
                        {estadosDisponibles.map((e) => (
                            <MenuItem key={e.id} value={e.id}>
                                <Stack direction="row" alignItems="center" gap={1}>
                                    <Box
                                        sx={{
                                            width: 10, height: 10,
                                            borderRadius: "50%",
                                            backgroundColor: e.color,
                                            flexShrink: 0,
                                        }}
                                    />
                                    {e.descripcion}
                                </Stack>
                            </MenuItem>
                        ))}
                    </TextField>
                )}

                {/* Adjuntos */}
                <Box>
                    <Button
                        component="label"
                        variant="outlined"
                        startIcon={<AttachFileIcon />}
                        size="small"
                        disabled={archivos.length >= 5}
                    >
                        Adjuntar archivos
                        <input type="file" multiple hidden onChange={handleArchivos} />
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        Máx. 5 archivos · 20 MB c/u
                    </Typography>

                    {archivos.length > 0 && (
                        <List dense sx={{ mt: 1 }}>
                            {archivos.map((f, idx) => {
                                const esImagen = f.type?.startsWith("image/");
                                const esPDF = f.type === "application/pdf";
                                const puedeVer = esImagen || esPDF;
                                const previewUrl = puedeVer ? URL.createObjectURL(f) : null;

                                return (
                                    <ListItem
                                        key={idx}
                                        disableGutters
                                        sx={{ borderBottom: "1px solid", borderColor: "divider", py: 0.5 }}
                                        secondaryAction={
                                            <span style={{ display: "flex", gap: 2 }}>
                                                {puedeVer && (
                                                    <Tooltip title="Ver archivo">
                                                        <IconButton size="small"
                                                            onClick={() => window.open(previewUrl, "_blank", "noopener,noreferrer")}>
                                                            <VisibilityIcon sx={{ fontSize: 15, color: "primary.main" }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title="Quitar">
                                                    <IconButton size="small" onClick={() => eliminarArchivo(idx)}>
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </span>
                                        }
                                    >
                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                            {esPDF
                                                ? <PictureAsPdfIcon sx={{ fontSize: 15, color: "#ef4444" }} />
                                                : esImagen
                                                    ? <ImageIcon sx={{ fontSize: 15, color: "#3b82f6" }} />
                                                    : <InsertDriveFileIcon fontSize="small" color="action" />}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                                                    {f.name}
                                                </Typography>
                                            }
                                            secondary={fmtSize(f.size)}
                                        />
                                    </ListItem>
                                );
                            })}
                        </List>
                    )}
                </Box>
            </DialogContent>

            <Divider />

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    variant="outlined" color="inherit"
                    onClick={handleClose} disabled={guardando}
                >
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    startIcon={
                        guardando
                            ? <CircularProgress size={16} color="inherit" />
                            : <SendIcon />
                    }
                    onClick={handleGuardar}
                    disabled={guardando}
                >
                    {guardando ? "Guardando..." : "Registrar"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}